import {
  parseDurationMinutes,
  formatDurationLabel,
  formatMinutesForStorage,
} from "./trainingDuration";
import { normalizeAppLang } from "./appLanguages";
import {
  localizedExerciseName,
  localizedTypeName,
  localizedSubTypeName,
  localizedCbName,
} from "./localizedCatalogFields";
import { isKingOfTableExerciseType, isKotExercise } from "./teExecutionType";
import { isLiveTimerBlock } from "./teCoachTools";

export { formatDurationLabel, formatMinutesForStorage, parseDurationMinutes };

export const PLANNING_TSG_STORAGE_KEY = "planning_tsg_id";

export const SUBGROUP_COLORS = [
  "#0877BC",
  "#E85D04",
  "#2A9D8F",
  "#7B2CBF",
  "#C1121F",
  "#F4A261",
];

/** Accent colors for exercise-sync clusters (distinct from station palette). */
export const EXERCISE_SYNC_GROUP_COLORS = [
  "#0D9488",
  "#CA8A04",
  "#0369A1",
  "#B45309",
  "#047857",
  "#9A3412",
];

export const TIMER_MODES = {
  SYNCED: "synced",
  INDEPENDENT: "independent",
};

export function persistPlanningTsgId(tsgId) {
  try {
    if (tsgId) sessionStorage.setItem(PLANNING_TSG_STORAGE_KEY, String(tsgId));
    else sessionStorage.removeItem(PLANNING_TSG_STORAGE_KEY);
  } catch (e) {}
}

/** Snapshot Pingflo reads so "this station" / "this block" maps to the board. */
export const PLANNING_FOCUS_KEY = "planning_focus";

export function persistPlanningFocus(focus) {
  try {
    if (!focus) {
      sessionStorage.removeItem(PLANNING_FOCUS_KEY);
    } else {
      sessionStorage.setItem(PLANNING_FOCUS_KEY, JSON.stringify(focus));
    }
    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent("smarttt:planning-focus", { detail: focus || null })
      );
    }
  } catch (e) {}
}

export function readPlanningFocus() {
  try {
    const raw = sessionStorage.getItem(PLANNING_FOCUS_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export const PLANNING_REFRESH_EVENT = "smarttt:planning-refresh";

export function requestPlanningRefresh() {
  try {
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent(PLANNING_REFRESH_EVENT));
    }
  } catch (e) {}
}

export function readPlanningTsgId() {
  try {
    return sessionStorage.getItem(PLANNING_TSG_STORAGE_KEY) || null;
  } catch (e) {
    return null;
  }
}

export function exerciseMinutes(ex) {
  const seqs = ex?.sequences;
  if (Array.isArray(seqs) && seqs.length > 0) {
    return seqs.reduce(
      (sum, seq) => sum + parseDurationMinutes(seq?.sequence_duration),
      0
    );
  }
  return parseDurationMinutes(ex?.te_duration);
}

export function waveKey(ex) {
  if (ex?.te_wave != null && ex.te_wave !== "") return Number(ex.te_wave);
  return Number(ex?.te_order ?? 0);
}

export const BLOCKS_OF_COMPETENCY_TYPE_EN = "Blocks of competency";

export function isPlaceholderBlock(ex) {
  return !ex || ex.ex_id == null || ex.ex_id === "";
}

/** Catalog drill is pinned on the block. */
export function hasCatalogExercise(ex) {
  return Boolean(ex) && ex.ex_id != null && ex.ex_id !== "";
}

function isPersonalizedSlotRow(ex) {
  const typeName = String(
    ex?.type_ex?.type_name_en || ex?.tbl_type_ex?.type_name_en || ""
  )
    .trim()
    .toLowerCase();
  return typeName === "personalized" || ex?.personalized_slot === true;
}

function competencyKindSource(ex) {
  if (!ex) return null;
  const seq0 = Array.isArray(ex.sequences) ? ex.sequences[0] : null;
  return (
    ex.competency_block ||
    ex.blocks_compentency ||
    ex.tbl_blocks_compentency ||
    (ex.ex_sub_type_id && typeof ex.ex_sub_type_id === "object"
      ? ex.ex_sub_type_id
      : null) ||
    seq0?.competency_block ||
    seq0?.blocks_compentency ||
    null
  );
}

/** True when the block's exercise type is Blocks of competency. */
function isGenericCompetencySubtypeName(name) {
  const n = String(name || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
  if (!n) return false;
  return (
    n === "competency block" ||
    n === "competency blocks" ||
    n === "bloc de competence" ||
    n === "blocs de competence" ||
    n === "bloque de competencia" ||
    n === "bloques de competencia" ||
    n === "kompetenzblock" ||
    n === "kompetenzbloecke" ||
    n === "kompetenzblöcke" ||
    n === BLOCKS_OF_COMPETENCY_TYPE_EN.toLowerCase()
  );
}

export function isCompetencyBlockSlot(ex) {
  const typeObj = ex?.type_ex || ex?.tbl_type_ex || ex?.capture_type_ex;
  if (typeObj?.type_name_en === BLOCKS_OF_COMPETENCY_TYPE_EN) return true;
  // Type-only CB placeholders sometimes only carry the CB id/object.
  if (!typeObj && (competencyKindSource(ex) || ex?.cb_id)) return true;
  return false;
}

/** Competency-block id currently set on a training block (CB-typed blocks only). */
export function competencyBlockIdOf(ex) {
  if (!isCompetencyBlockSlot(ex)) return null;
  const src = competencyKindSource(ex);
  if (src?.cb_id) return src.cb_id;
  if (typeof ex.ex_sub_type_id === "string" && ex.ex_sub_type_id) {
    return ex.ex_sub_type_id;
  }
  const seq0 = Array.isArray(ex.sequences) ? ex.sequences[0] : null;
  if (typeof seq0?.sub_type_id === "string" && seq0.sub_type_id) {
    return seq0.sub_type_id;
  }
  return seq0?.sub_type_id?.cb_id || null;
}

function competencyKindName(ex, lng = "en") {
  const base = normalizeAppLang(lng);
  const seq0 = Array.isArray(ex?.sequences) ? ex.sequences[0] : null;
  const fromCb = localizedCbName(competencyKindSource(ex), base);
  if (fromCb) return fromCb;
  const subtypeCandidates = [
    localizedSubTypeName(ex?.sub_type_ex, base),
    localizedSubTypeName(ex?.sub_type_ex_direct, base),
    localizedSubTypeName(seq0?.sub_type_ex, base),
    localizedSubTypeName(seq0, base),
  ];
  for (const name of subtypeCandidates) {
    if (name && !isGenericCompetencySubtypeName(name)) return name;
  }
  return "";
}

/**
 * Type (and optional subtype / competency-block kind) is set, catalog drill is not.
 * The coach assigns the exercise during the session or afterwards.
 * Personalized slots are their own mode and are not type-only.
 */
export function isTypeOnlyBlock(ex) {
  if (!ex || !isPlaceholderBlock(ex)) return false;
  if (isPersonalizedSlotRow(ex)) return false;
  // Live timer blocks are chrono-only containers — no catalog pick.
  if (isLiveTimerBlock(ex)) return false;
  // KoT runs from the type itself (Match Simulation by default) — no catalog pick.
  if (
    isKotExercise(ex) ||
    isKingOfTableExerciseType(ex.type_ex || ex.tbl_type_ex || ex.capture_type_ex)
  ) {
    return false;
  }
  return Boolean(
    ex.type_id ||
      ex.type_ex?.type_id ||
      ex.type_ex?.type_name_en ||
      ex.tbl_type_ex?.type_id ||
      ex.sub_type_ex?.sub_type_id ||
      ex.sub_type_id ||
      ex.ex_sub_type_id ||
      ex.cb_id ||
      competencyKindSource(ex) ||
      ex.sequences?.[0]?.sub_type_id
  );
}

export function exerciseLabel(ex, lng = "en") {
  if (!ex) return "";
  const base = normalizeAppLang(lng);
  return (
    localizedExerciseName(ex, base) ||
    competencyKindName(ex, base) ||
    localizedTypeName(ex.type_ex || ex.tbl_type_ex, base) ||
    localizedSubTypeName(ex.sub_type_ex, base) ||
    ""
  );
}

/**
 * Line shown at the top of a training-block card, next to the block number.
 * Competency-block slots use the block name. Every other type uses the exercise type.
 */
export function blockTypeHeadline(ex, lng = "en") {
  if (!ex) return "";
  const base = normalizeAppLang(lng);
  const typeObj = ex.type_ex || ex.tbl_type_ex || ex.capture_type_ex;
  if (isCompetencyBlockSlot(ex)) {
    return competencyKindName(ex, base) || localizedTypeName(typeObj, base);
  }
  return localizedTypeName(typeObj, base) || "";
}

/** Subtype or competency-block kind for a training-block chip. */
export function blockKindLabel(ex, lng = "en") {
  if (!ex) return "";
  const base = normalizeAppLang(lng);
  const typeObj = ex.type_ex || ex.tbl_type_ex || ex.capture_type_ex;
  if (isCompetencyBlockSlot(ex)) {
    return competencyKindName(ex, base) || localizedTypeName(typeObj, base);
  }
  const subName = localizedSubTypeName(ex.sub_type_ex, base);
  if (subName && !isGenericCompetencySubtypeName(subName)) return subName;
  return localizedTypeName(typeObj, base) || "";
}

/** Name for a planned block: catalog drill, else type or competency-block kind. */
export function plannedBlockTitle(ex, lng = "en") {
  if (!ex || isPersonalizedSlotRow(ex)) return "";
  if (hasCatalogExercise(ex)) {
    const named = localizedExerciseName(ex, lng);
    if (named) return named;
  }
  return blockTypeHeadline(ex, lng) || blockKindLabel(ex, lng) || "";
}

export function sequenceCount(ex) {
  return Array.isArray(ex?.sequences) ? ex.sequences.length : 0;
}

export function buildPlanningLanes({ subgroups = [], exercises = [] } = {}) {
  const stations = Array.isArray(subgroups) ? subgroups : [];
  const list = (exercises || []).filter(Boolean);
  const knownIds = new Set(stations.map((s) => String(s.tsg_id)));

  const lanes = stations.map((sg) => ({
    ...sg,
    timer_mode: sg.timer_mode === TIMER_MODES.INDEPENDENT
      ? TIMER_MODES.INDEPENDENT
      : TIMER_MODES.SYNCED,
    exercises: list
      .filter((ex) => String(ex.tsg_id || "") === String(sg.tsg_id))
      .sort((a, b) => waveKey(a) - waveKey(b) || (a.te_order ?? 0) - (b.te_order ?? 0)),
  }));

  const unassigned = list.filter((ex) => !ex.tsg_id || !knownIds.has(String(ex.tsg_id)));
  if (lanes.length === 0) {
    return [
      {
        tsg_id: "main-fallback",
        name: "Main group",
        color: "#0877BC",
        timer_mode: TIMER_MODES.SYNCED,
        is_main: true,
        tables: [],
        exercises: list
          .slice()
          .sort((a, b) => (a.te_order ?? 0) - (b.te_order ?? 0)),
      },
    ];
  }
  if (unassigned.length && lanes[0]) {
    lanes[0] = {
      ...lanes[0],
      exercises: [...lanes[0].exercises, ...unassigned].sort(
        (a, b) => waveKey(a) - waveKey(b) || (a.te_order ?? 0) - (b.te_order ?? 0)
      ),
    };
  }
  return lanes;
}

export function wallClockMinutes(lanes) {
  const list = Array.isArray(lanes) ? lanes : [];
  const synced = list.filter((l) => l.timer_mode !== TIMER_MODES.INDEPENDENT);
  const independent = list.filter((l) => l.timer_mode === TIMER_MODES.INDEPENDENT);
  const waveMax = new Map();
  for (const lane of synced) {
    for (const ex of lane.exercises || []) {
      const w = waveKey(ex);
      waveMax.set(w, Math.max(waveMax.get(w) || 0, exerciseMinutes(ex)));
    }
  }
  const syncedTotal = [...waveMax.values()].reduce((a, b) => a + b, 0);
  const indepMax = independent.reduce((max, lane) => {
    const sum = (lane.exercises || []).reduce((s, ex) => s + exerciseMinutes(ex), 0);
    return Math.max(max, sum);
  }, 0);
  return Math.max(syncedTotal, indepMax);
}

export function waveDurationMismatches(lanes) {
  const synced = (lanes || []).filter((l) => l.timer_mode !== TIMER_MODES.INDEPENDENT);
  if (synced.length < 2) return [];
  const byWave = new Map();
  for (const lane of synced) {
    for (const ex of lane.exercises || []) {
      const w = waveKey(ex);
      if (!byWave.has(w)) byWave.set(w, []);
      byWave.get(w).push({
        tsg_id: lane.tsg_id,
        minutes: exerciseMinutes(ex),
        te_id: ex.te_id,
      });
    }
  }
  const mismatches = [];
  for (const [wave, entries] of byWave) {
    const mins = entries.map((e) => e.minutes);
    const lo = Math.min(...mins);
    const hi = Math.max(...mins);
    if (hi !== lo) mismatches.push({ te_wave: wave, min: lo, max: hi, entries });
  }
  return mismatches;
}

export function mismatchTeIds(mismatches) {
  const ids = new Set();
  for (const m of mismatches || []) {
    for (const e of m.entries || []) ids.add(String(e.te_id));
  }
  return ids;
}

/**
 * Blocks "on sync": same non-empty ex_id on the same te_wave across 2+ stations.
 * No persisted flag — derived from planning lanes after syncBlockExercise (or manual match).
 * Returns Map(te_id -> { groupIndex, color, wave, exId, stationCount }).
 */
export function buildExerciseSyncLookup(lanes) {
  const clusters = new Map();
  for (const lane of lanes || []) {
    const tsgId = String(lane?.tsg_id || "");
    for (const ex of lane?.exercises || []) {
      if (isPlaceholderBlock(ex)) continue;
      const exId = String(ex.ex_id);
      const wave = waveKey(ex);
      const key = `${wave}::${exId}`;
      if (!clusters.has(key)) {
        clusters.set(key, { wave, exId, entries: [] });
      }
      clusters.get(key).entries.push({
        te_id: ex.te_id,
        tsg_id: tsgId || String(ex.tsg_id || ""),
      });
    }
  }

  const synced = [...clusters.entries()]
    .filter(([, cluster]) => {
      const stations = new Set(
        cluster.entries.map((e) => String(e.tsg_id || "")).filter(Boolean)
      );
      return stations.size >= 2;
    })
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));

  const byTeId = new Map();
  synced.forEach(([, cluster], groupIndex) => {
    const color =
      EXERCISE_SYNC_GROUP_COLORS[groupIndex % EXERCISE_SYNC_GROUP_COLORS.length];
    const stationCount = new Set(
      cluster.entries.map((e) => String(e.tsg_id || "")).filter(Boolean)
    ).size;
    for (const entry of cluster.entries) {
      byTeId.set(String(entry.te_id), {
        groupIndex,
        color,
        wave: cluster.wave,
        exId: cluster.exId,
        stationCount,
      });
    }
  });
  return byTeId;
}

export function laneDuration(lane) {
  return (lane?.exercises || []).reduce((s, ex) => s + exerciseMinutes(ex), 0);
}

const LOCKED_TE_STATES = new Set([
  "st202406071447279901",
  "st202406071447279902",
  "st202406071447279903",
  "st202406071447279904",
]);

const TRAINING_BLOCK_POSITION_KEYS = ["tsg_id", "te_wave", "te_order"];

export function teStateIdOf(ex) {
  if (!ex) return null;
  if (typeof ex.te_state === "string") return ex.te_state;
  return ex.tbl_state_te?.st_id || ex.te_state?.st_id || null;
}

const FINALIZED_TE_STATE = "st202406071447279903";

export function isPlanningExerciseFinalized(ex, liveStateId) {
  const id = liveStateId || teStateIdOf(ex);
  if (id == null) return false;
  const s = String(id).trim().toLowerCase();
  return s === FINALIZED_TE_STATE || s === "finalized" || s === "finished";
}

export function isPlanningBlockLocked(ex, liveStateId) {
  const id = liveStateId || teStateIdOf(ex);
  return id != null && LOCKED_TE_STATES.has(String(id));
}

/** Two chips can trade places. The exercise stays on its training block. */
export function canSwapPlanningBlockExercises(fromEx, toEx, { fromLocked, toLocked } = {}) {
  if (!fromEx || !toEx) return false;
  if (String(fromEx.te_id) === String(toEx.te_id)) return false;
  if (fromLocked || toLocked) return false;
  if (fromLocked == null && isPlanningBlockLocked(fromEx)) return false;
  if (toLocked == null && isPlanningBlockLocked(toEx)) return false;
  return true;
}

function pickPositionFields(ex) {
  const out = {};
  for (const key of TRAINING_BLOCK_POSITION_KEYS) {
    out[key] = ex ? ex[key] : undefined;
  }
  return out;
}

/** Swap station / wave / order; keep te_id with its exercise and sequences. */
export function swapTrainingBlockExercisesInList(list, fromTeId, toTeId) {
  const rows = Array.isArray(list) ? list : [];
  const i = rows.findIndex((ex) => String(ex.te_id) === String(fromTeId));
  const j = rows.findIndex((ex) => String(ex.te_id) === String(toTeId));
  if (i < 0 || j < 0 || i === j) return rows;
  const fromEx = rows[i];
  const toEx = rows[j];
  const next = rows.slice();
  next[i] = { ...fromEx, ...pickPositionFields(toEx) };
  next[j] = { ...toEx, ...pickPositionFields(fromEx) };
  return next;
}

export function timeTicks(totalMinutes, step = 15) {
  const max = Math.max(Number(totalMinutes) || 0, step);
  const ticks = [];
  for (let m = 0; m <= max; m += step) ticks.push(m);
  if (ticks[ticks.length - 1] !== max) ticks.push(max);
  return ticks;
}

/** Apply a duration to every synced-station block on the same wave. */
export function mapListWithSyncedDuration(
  list,
  sourceEx,
  durationStr,
  subgroups,
  distributeFn,
  { includeSource = true } = {}
) {
  const rows = Array.isArray(list) ? list : [];
  if (!sourceEx) return rows;
  const syncedIds = new Set(
    (subgroups || [])
      .filter((s) => s.timer_mode !== TIMER_MODES.INDEPENDENT)
      .map((s) => String(s.tsg_id))
  );
  const sourceSynced = syncedIds.has(String(sourceEx.tsg_id || ""));
  const wave = waveKey(sourceEx);
  return rows.map((item) => {
    const isSource = String(item.te_id) === String(sourceEx.te_id);
    if (isSource && !includeSource) return item;
    const isPeer =
      sourceSynced &&
      !isSource &&
      syncedIds.has(String(item.tsg_id || "")) &&
      waveKey(item) === wave;
    if (!isSource && !isPeer) return item;
    const next = { ...item, te_duration: durationStr };
    if (item.sequences?.length > 0 && typeof distributeFn === "function") {
      next.sequences = distributeFn(durationStr, item.sequences);
    }
    return next;
  });
}
